import React, { useCallback, useEffect, useState } from "react";
import styled from "@emotion/styled";

import Form from "react-bootstrap/Form";
import { useForm } from "react-hook-form";

import { Button } from "@/components/Buttons";
import Modal from "@/components/Modal";
import PrintContent from "@/components/Print/PrintContent";
import PrintPortal from "@/components/Print/PrintPortal";

import {
  CORP_USER_NAMES_KOOKMIN,
  DeliveryForWaybill,
} from "@/Models/DeliveryForWaybill";

import {
  useAllocationGroupsStore,
  useDeliveriesStore,
  useModalStore,
} from "@/store/hooks";

import {
  SIZE_TYPE_150_V3,
  SIZE_TYPE_150_V3_BLANK,
  SIZE_TYPE_150_HANJIN,
} from "@/utils/Print";

const Title = styled.h3`
  margin: 0;
`;

const FormLabel = styled(Form.Label)`
  font-size: 16px;
  margin-bottom: 10px;
  font-weight: 500;
`;

const ButtonWrap = styled.div`
  margin-top: 40px;
  text-align: right;
`;
const ModalButton = styled(Button)`
  margin-left: 5px;
`;

const WaybillModal = ({
  deliveries,
  onAfterSubmit = () => {},
  defaultSizeType = SIZE_TYPE_150_V3,
  hanjinOnly = false,
}) => {
  const { allocationGroupsCache } = useAllocationGroupsStore().state;
  const { ...allocationGroupsActions } = useAllocationGroupsStore();

  const { closeModal } = useModalStore();

  const { ...actions } = useDeliveriesStore();

  const { getValues, handleSubmit, register, reset } = useForm();

  const [showPrintWindow, setShowPrintWindow] = useState(false);

  const [deliveriesWithAllocationGroups, setDeliveriesWithAllocationGroups] =
    useState([]);
  const [refined, setRefined] = useState(false);

  useEffect(() => {
    reset({
      sizeType: hanjinOnly ? SIZE_TYPE_150_HANJIN : defaultSizeType,
    });
  }, [defaultSizeType, hanjinOnly, reset]);

  useEffect(() => {
    refineAllocationGroups();
  }, []);

  const refineAllocationGroups = useCallback(async () => {
    const _deliveriesWithAllocationGroups = [...deliveries];
  
    await Promise.allSettled(
      _deliveriesWithAllocationGroups.map(async (delivery) => {
        // sender와 receiver를 병렬로 처리
        await Promise.allSettled([
          // sender 처리
          (async () => {
            if (delivery.senderPostalCode) {
              const _allocationGroupCache = allocationGroupsCache.find(
                (c) =>
                  c.postalCode === delivery.senderPostalCode &&
                  c.corpUserId === delivery.corpUser?.id,
              );
              
              const _allocationGroup = _allocationGroupCache || 
                await allocationGroupsActions.fetchByPostalCode({
                  postalCode: delivery.senderPostalCode,
                  corpUserId: delivery.corpUser?.id,
                });
  
              delivery.senderAllocationGroupName = _allocationGroup?.name;
            }
          })(),
          // receiver 처리
          (async () => {
            if (delivery.receiverPostalCode) {
              const _allocationGroupCache = allocationGroupsCache.find(
                (c) =>
                  c.postalCode === delivery.receiverPostalCode &&
                  c.corpUserId === delivery.corpUser?.id,
              );
              
              const _allocationGroup = _allocationGroupCache || 
                await allocationGroupsActions.fetchByPostalCode({
                  postalCode: delivery.receiverPostalCode,
                  corpUserId: delivery.corpUser?.id,
                });
  
              delivery.receiverAllocationGroupName = _allocationGroup?.name;
            }
          })(),
        ]);
      }),
    );
  
    const _deliveriesRefined = _deliveriesWithAllocationGroups.filter(
      (delivery) => {
        return (
          delivery.corpUser?.username !== CORP_USER_NAMES_KOOKMIN ||
          delivery.productName !== "Liiv M 유심"
        );
      },
    );
  
    setDeliveriesWithAllocationGroups(_deliveriesRefined);
    setRefined(true);
  }, [allocationGroupsCache, deliveries]);

  const handlePrint = useCallback(
    async (_deliveries, onAfterPrint = () => {}) => {
      try {
        const result = await actions.printedBulk(_deliveries);

        const failedDeliveries = [];
        result.forEach((el, index) => {
          if (el.status === "rejected" || el.value?.success === false) {
            failedDeliveries.push(_deliveries[index]);
          }
        });

        if (failedDeliveries.length > 0) {
          const successCount = _deliveries.length - failedDeliveries.length;

          if (
            window.confirm(
              `운송장 출력: ${successCount}건 성공, ${failedDeliveries.length}건 실패\n` +
                `실패한 배송을 다시 전송하시겠습니까?\n` +
                `실패한 배송번호: ${failedDeliveries
                  .map((el) => el.bookId)
                  .join(", ")}`,
            )
          ) {
            await handlePrint(failedDeliveries, onAfterPrint);
            return;
          }

          onAfterPrint();
          return;
        }

        onAfterPrint();
      } catch (e) {
        window.alert(`운송장 출력에 실패하였습니다.\n${e.message}`);
      }
    },
    [actions],
  );

  const onSubmit = useCallback(
    async (_data) => {
      handlePrint(deliveries, () => {
        setShowPrintWindow(true);
        onAfterSubmit();
      });
    },
    [deliveries, onAfterSubmit, handlePrint],
  );

  const totalCount = deliveriesWithAllocationGroups.length || 0;
  const originalCount = deliveries?.length || 0;

  useEffect(() => {
    if (refined && totalCount === 0) {
      window.alert(`운송장 출력할 배송목록이 없습니다.`);
      closeModal();
    }
  }, [totalCount, refined]);

  return (
    <Modal size="md">
      <Modal.Header>
        <Title>
          운송장 출력 ({totalCount}
          {originalCount !== totalCount ? `/${originalCount}` : ""}건)
        </Title>
      </Modal.Header>
      <Modal.Body>
        <Form onSubmit={handleSubmit(onSubmit)}>
          <Form.Group>
            <FormLabel>사이즈 선택</FormLabel>
            <Form.Check
              label="100 x 150 한진 사이즈"
              type="radio"
              id="sizeType-150-hanjin"
              value={SIZE_TYPE_150_HANJIN}
              name="sizeType"
              ref={register}
            />
            {!hanjinOnly && (
              <>
                <Form.Check
                  label="100 x 150 사이즈 (신규)"
                  type="radio"
                  id="sizeType-150-v3"
                  value={SIZE_TYPE_150_V3}
                  name="sizeType"
                  ref={register}
                />
                <Form.Check
                  label="100 x 150 사이즈 (무지)"
                  type="radio"
                  id="sizeType-150-v3-blank"
                  value={SIZE_TYPE_150_V3_BLANK}
                  name="sizeType"
                  ref={register}
                />
              </>
            )}
          </Form.Group>
          <ButtonWrap>
            <ModalButton type="submit" width="120px">
              출력
            </ModalButton>
            <ModalButton
              onClick={closeModal}
              type="button"
              variant="outline-secondary"
              width="120px"
            >
              취소
            </ModalButton>
          </ButtonWrap>
        </Form>

        {showPrintWindow && (
          <PrintPortal sizeType={getValues().sizeType}>
            <PrintContent
              deliveries={deliveriesWithAllocationGroups.map(
                (d) => new DeliveryForWaybill(d, getValues().sizeType),
              )}
              sizeType={getValues().sizeType}
            />
          </PrintPortal>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default WaybillModal;